import { ArrowUpRight, BookOpen, Film, Fish, Waves, Compass, Star } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { DESTINATIONS } from "../../data/cottages";
import { Reveal } from "./Reveal";
import { LingonSprig } from "./SmalandDetails";

const HIGHLIGHTS = [
  {
    key: "alv",
    image: "https://commons.wikimedia.org/wiki/Special:Redirect/file/Villa_Villekulla_and_Pippi.jpg?width=1600",
    credit: "Foto: Chrumps / Wikimedia Commons · CC BY-SA 4.0",
    icon: Star,
  },
  {
    key: "swim",
    image: "https://images.unsplash.com/photo-1510798831971-661eb04b3739?auto=format&fit=crop&w=1400&q=84",
    icon: Waves,
  },
  {
    key: "fishing",
    image: "https://images.unsplash.com/photo-1720434896142-300e7289a34c?auto=format&fit=crop&w=1400&q=84",
    icon: Fish,
  },
];

const MORE_DESTINATIONS = ["nas", "filmbyn", "tourist"];
const MORE_ICONS = { nas: BookOpen, filmbyn: Film, tourist: Compass };
const MORE_IMAGES = {
  nas: {
    src: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/11/Astrid_Lindgrens_home_in_Vimmerby.JPG/1280px-Astrid_Lindgrens_home_in_Vimmerby.JPG",
    credit: "Foto: W.A. 2.0 / Wikimedia Commons · CC BY-SA 3.0",
  },
};

const highlightCopy = (a, key) => {
  if (key === "alv") {
    return {
      title: a.cards.alv.name,
      text: a.cards.alv.text,
      url: DESTINATIONS.find((item) => item.key === "alv")?.url,
      link: a.cards.alv.link,
    };
  }
  if (key === "swim") {
    return {
      title: a.nature[1].title,
      text: a.nature[1].text,
      url: DESTINATIONS.find((item) => item.key === "tourist")?.url,
      link: a.cards.tourist.link,
    };
  }
  return {
    title: a.nature[2].title,
    text: a.nature[2].text,
    url: DESTINATIONS.find((item) => item.key === "fishing")?.url,
    link: a.cards.fishing.link,
  };
};

const HighlightCard = ({ item, delay }) => {
  const { t } = useLang();
  const copy = highlightCopy(t.activities, item.key);
  const Icon = item.icon;
  return (
    <Reveal delay={delay} className="experience-card">
      <a href={copy.url} target="_blank" rel="noopener noreferrer" className="experience-card__link">
        <span className="experience-card__image-wrap">
          <img className="experience-card__image" src={item.image} alt="" loading="lazy" decoding="async" />
          {item.credit && <span className="experience-card__credit">{item.credit}</span>}
        </span>
        <span className="experience-card__body">
          <span className="experience-card__icon"><Icon size={22} strokeWidth={1.3} aria-hidden="true" /></span>
          <span className="experience-card__copy">
            <strong>{copy.title}</strong>
            <span>{copy.text}</span>
          </span>
          <span className="experience-card__arrow" aria-hidden="true"><ArrowUpRight size={20} strokeWidth={1.5} /></span>
          <span className="sr-only">{copy.link} ({t.cottages.newTab})</span>
        </span>
      </a>
    </Reveal>
  );
};

const MoreDestination = ({ dest, delay }) => {
  const { t } = useLang();
  const copy = t.activities.cards[dest.key];
  const Icon = MORE_ICONS[dest.key];
  const image = MORE_IMAGES[dest.key];

  return (
    <Reveal delay={delay} className="activities-more__item">
      <a
        href={dest.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`activities-more__link ${image ? "activities-more__link--image" : ""}`}
      >
        {image ? (
          <span className="activities-more__thumb">
            <img src={image.src} alt="" loading="lazy" decoding="async" />
            <small>{image.credit}</small>
          </span>
        ) : (
          <span className="activities-more__icon"><Icon size={19} strokeWidth={1.35} aria-hidden="true" /></span>
        )}
        <span className="activities-more__copy">
          <strong>{copy.name}</strong>
          <span>{copy.text}</span>
        </span>
        <ArrowUpRight className="activities-more__arrow" size={17} strokeWidth={1.5} aria-hidden="true" />
        <span className="sr-only">({t.cottages.newTab})</span>
      </a>
    </Reveal>
  );
};

export const Activities = () => {
  const { t } = useLang();
  const a = t.activities;
  return (
    <section id="aktiviteter" className="section activities" data-testid="section-aktiviteter">
      <div className="wrap">
        <div className="activities__showcase-head">
          <div className="activities__head-title">
            <p className="eyebrow">{a.eyebrow}</p>
            <h2 className="section-title" data-testid="activities-heading">{a.title}</h2>
          </div>
          <p className="activities__head-lead">{a.lead}</p>
          <LingonSprig className="activities__lingon" />
        </div>

        <div className="experience-grid">
          {HIGHLIGHTS.map((item, i) => <HighlightCard key={item.key} item={item} delay={i * 80} />)}
        </div>

        <div className="activities-more">
          <div className="activities-more__head">
            <h3>{a.destinationsTitle}</h3>
            <span aria-hidden="true" />
          </div>
          <div className="activities-more__grid">
            {MORE_DESTINATIONS.map((key, i) => {
              const dest = DESTINATIONS.find((item) => item.key === key);
              return <MoreDestination key={key} dest={dest} delay={i * 70} />;
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
